// built-from: /activity-contract-pool v1 impl:activity-contract-pool role:implementation
// built-from: /shared-process-spine v1 impl:shared-process-spine role:implementation
// built-from: /process-spine-surface v1 impl:process-spine-surface role:implementation
// Features 083 + 085: the Event Storming activity screen — the one chat
// streams the step-flow grammar (chat2 context 'event-storming', no tool
// calls) and the capture renders on the Process Blueprint canvas: the step
// ribbon as the temporal axis, the domain overlay's swimlanes (actors /
// bounded contexts / systems) filling in below, aligned under their steps.
// The source-edit modal remains the direct-edit path (including re-parenting
// via the `:: in` clause). The shared process spine keeps receiving capture
// syncs and colours the ribbon's review states; its separate card-strip
// surface and one-way refine phase are retired (owner direction 2026-08-31).

import { Component, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import { FacilitatorDock } from '../components/FacilitatorDock';
import { ActivityShell } from '../facilitation/ActivityShell';
import {
  PendingActivityConversation,
  useFirstMessageRelay,
  type ActivityFacilitationBinding,
} from '../facilitation/ActivityConversation';
import { rejectedCaptureText } from '../utils/rejectedCapture';
import SessionCapturePanel from '../components/SessionCapturePanel';
import FlowSourceEditModal, { type FlowSourceSaveArgs, type FlowSourceSaveResult } from '../components/FlowSourceEditModal';
import FlowCanvas from '../components/FlowCanvas';
import StepCardRibbon from '../components/StepCardRibbon';
import ProcessNavigation from '../components/ProcessNavigation';
import ProcessLandscape, { type LandscapeFilters, type LandscapeView } from '../components/ProcessLandscape';
import { useShellSidebar } from '../components/AppShell';
import { UNPLACED_LABEL, type LandscapeGlossary, type LandscapeImportance } from '../utils/processLandscape';
import type { DomainsDocument } from './OutlineSurfaces';
import { useSession } from '../hooks/useSession';
import { useEngagement } from '../hooks/useEngagement';
import { parseStepFlow } from '../utils/stepFlow';
import {
  WALK_ORDER,
  deriveWalkGroups,
  humaniseName,
  partOfStep,
  planAcceptance,
  stepIdsOfPart,
} from '../utils/domainWalk';
import type { EngagementActivity } from '../hooks/useEngagementSetup';
import type { EngagementSetupModel } from './EngagementSetupWorkspace';
import { NavigateToActivity, useEnsuredActivitySession, useEngagementDoc, useProcessDocument, useProcessRegister } from './BriefFirstWorkspaces';
import '../styles/BriefFirstWorkspace.css';
import '../styles/EventStormingWorkspace.css';

// 096: the register entries the business map enriches (095). Optional
// here so the screen reads them when present and stays whole when not.
type RegisterExtras = { domainId?: string | null; importance?: LandscapeImportance | null; rank?: number | null };

// The facilitator's scope while the landscape is showing (096): the
// server's overview sentinel, never a process.
const OVERVIEW_SCOPE = 'overview';

// A render fault in the flow view must degrade to a message, never blank
// the workspace — the chat and the captured flow stay usable (the
// 2026-08-25 blank-screen defect class). Resets when the flow text changes
// so the next turn retries the render.
class FlowViewBoundary extends Component<
  { resetKey: string; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidUpdate(previous: { resetKey: string }) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (this.state.failed) {
      return (
        <p className="outline-plan-empty" role="alert">
          The flow view hit a problem rendering. The captured flow is safe;
          keep talking, or open the flow source to check it.
        </p>
      );
    }
    return this.props.children;
  }
}

export function EngagementEventStormingWorkspace({ model, activityId }: {
  model: EngagementSetupModel;
  activityId: string;
}) {
  const { activity, matches, facilitation } = useEnsuredActivitySession(model, activityId, 'event-storming');
  if (!activity || !matches) return <NavigateToActivity activityId={activityId} />;
  // 100, decision 10: the workshop and its conversation open together. The
  // session arrives with the first message, never with the navigation.
  return <EventStormingSession key={facilitation.sessionId} facilitation={facilitation} activity={activity} model={model} />;
}

export function EventStormingSession({ facilitation, activity, model, embedded = false, onDocumentChange, selectedStepId }: {
  facilitation: ActivityFacilitationBinding;
  activity: EngagementActivity;
  model: EngagementSetupModel;
  embedded?: boolean;
  onDocumentChange?: () => void;
  selectedStepId?: number | null;
}) {
  // The session hook binds at mount (the standalone screen's pattern), so the
  // screen is keyed by the session id above: before the first message there is
  // no session and the shared not-yet-opened conversation stands in its place.
  const sessionId = facilitation.sessionId;
  const session = useSession(sessionId, 'discovery', { contextId: 'event-storming' });
  // The consultant's first message created this session; send it once the
  // session has finished loading, through the same chat every turn uses.
  useFirstMessageRelay(
    session.chat,
    facilitation.firstIntent,
    facilitation.clearFirstIntent,
    Boolean(sessionId) && !session.sessionLoading,
  );
  const [sourceOpen, setSourceOpen] = useState(false);
  // 089: "one process, one document" — the process document is the ONLY
  // render source for the step layer, walk band, canvas and source modal
  // (Architect ruling, client precedence). The session is conversation
  // plus focus; it no longer carries the flow, and the shared process
  // spine sync is retired for this screen entirely.
  const { currentEngagementId, currentEngagement } = useEngagement();
  const { register, glossary, refresh: refreshRegister, createProcess, renameProcess, removeProcess, restoreProcess, reorderProcess } =
    useProcessRegister(currentEngagementId);
  // 096: the business map's domains name the sidebar's groups and the
  // landscape's domain filter; a process with none sits under "Not placed
  // yet".
  const { doc: domainsDoc } = useEngagementDoc<DomainsDocument>(currentEngagementId, 'domains');
  const domains = useMemo(
    () => (domainsDoc?.domains ?? []).map((domain) => ({ id: domain.id, name: domain.name })),
    [domainsDoc],
  );

  // 096: the URL is the screen's navigation state, read and written through
  // the router so Browser Back works: `view=landscape` or `process=<id>`,
  // plus the landscape's filters. Choosing a view or a process PUSHES a
  // history entry; automatic focus resolution and filter changes REPLACE
  // the current one, so Back never steps through keystrokes.
  const [searchParams, setSearchParams] = useSearchParams();
  const view: 'process' | 'landscape' = !searchParams.get('process') && ['landscape', 'connections'].includes(searchParams.get('view') || '') ? 'landscape' : 'process';
  const focusedProcessId = searchParams.get('process');
  const landscapeFilters = useMemo<LandscapeFilters>(() => ({
    query: searchParams.get('q') ?? '',
    domain: searchParams.get('domain') ?? '',
    who: searchParams.get('who') ?? '',
    openOnly: searchParams.get('open') === '1',
    systems: searchParams.get('systems') === '1',
    mode: searchParams.get('mode') === 'table' ? 'table' : 'map',
  }), [searchParams]);
  // The map's zoom and scroll survive a trip into a process and back.
  const landscapeView: LandscapeView = { zoom: Number(searchParams.get('zoom')) || 1, left: Number(searchParams.get('left')) || 0, top: Number(searchParams.get('top')) || 0 };

  const setFocusedProcessId = useCallback((processId: string | null) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (processId) next.set('process', processId); else next.delete('process');
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const chooseProcess = (processId: string) => {
    setSearchParams(previous => { const next = new URLSearchParams(previous); next.set('process', processId); return next; });
  };
  const openLandscape = () => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('view', 'landscape');
      next.delete('process');
      next.delete('method');
      return next;
    });
  };
  const changeLandscapeFilters = (next: LandscapeFilters) => {
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      params.set('view', 'landscape');
      const write = (key: string, value: string) => { if (value) params.set(key, value); else params.delete(key); };
      write('q', next.query);
      write('domain', next.domain);
      write('who', next.who);
      write('open', next.openOnly ? '1' : '');
      write('systems', next.systems ? '1' : '');
      write('mode', next.mode === 'table' ? 'table' : '');
      return params;
    }, { replace: true });
  };

  // The facilitator's scope follows the screen (093, 096): the focused
  // process, or the overview while the landscape is showing. One POST per
  // change, whichever way the screen got there — a click, a deep link or
  // Browser Back.
  const scope = view === 'landscape' ? OVERVIEW_SCOPE : focusedProcessId;
  const postedScope = useRef<string | null>(null);
  useEffect(() => {
    if (embedded) return; // Business map binds and verifies focus before mounting this workspace.
    if (!scope || scope === postedScope.current) return;
    postedScope.current = scope;
    try {
      void fetch(`/api/sessions/${encodeURIComponent(sessionId)}/focus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ processId: scope }),
      });
    } catch {
      // Silent: the next turn's load resolves focus from the session anyway.
    }
  }, [scope, sessionId, embedded]);

  // The session's own focus (server-resolved: narration else the first live
  // process — the same precedence `loadState` applies). Fetched once on
  // mount and re-fetched after every turn (a New Process reply moves it
  // server-side); one helper backs both reads so they can't drift.
  const [sessionFocusProcessId, setSessionFocusProcessId] = useState<string | null | undefined>(undefined);
  const fetchSessionFocus = async (): Promise<string | null> => {
    try {
      const response = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}`);
      if (response.ok) {
        const body = await response.json() as { focusProcessId?: string | null };
        return body.focusProcessId || null;
      }
    } catch {
      // Silent: focus stays as last known; the next attempt retries.
    }
    return null;
  };
  const sessionFocusFetchedOnMount = useRef(false);
  useEffect(() => {
    if (sessionFocusFetchedOnMount.current) return;
    sessionFocusFetchedOnMount.current = true;
    void fetchSessionFocus().then((focusProcessId) => setSessionFocusProcessId(focusProcessId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolves focus whenever the register or the session's own focus
  // changes. Order: `?process=` (already the initial `focusedProcessId`
  // value) if it names a live process; else the session's own focus if
  // live; else the single live process when there is exactly one; else
  // none — the switcher prompts. A focus naming a process that is no
  // longer live (removed, or stale from a prior visit) is cleared first.
  useEffect(() => {
    if (embedded || view === 'landscape') return;
    const live = register.processes.filter((process) => !process.removedAt).sort((a, b) => a.order - b.order);
    if (live.length === 0) {
      if (focusedProcessId !== null) setFocusedProcessId(null);
      return;
    }
    if (focusedProcessId && live.some((process) => process.processId === focusedProcessId)) return;
    if (sessionFocusProcessId && live.some((process) => process.processId === sessionFocusProcessId)) {
      setFocusedProcessId(sessionFocusProcessId);
    } else if (live.length === 1) {
      setFocusedProcessId(live[0].processId);
    } else if (focusedProcessId) {
      setFocusedProcessId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register.processes, sessionFocusProcessId, embedded, view]);

  const {
    doc, revision: docRevision, refresh: refreshProcessDoc, undo, takeCheckpoint, movePart, lastError: processError, clearError: clearProcessError,
  } = useProcessDocument(currentEngagementId, focusedProcessId);
  useEffect(() => { setSourceOpen(false); }, [currentEngagementId, focusedProcessId]);

  // After every completed turn, re-read the session's focus (a New Process
  // reply moves it server-side) and then the focused document — the raw
  // reply changes exactly once per turn; the delayed second read covers
  // the mirror landing just after the stream closes.
  const turnRaw = session.newModel.raw || '';
  const userTurnCount = session.conversationHistory.filter((message) => message.type === 'user').length;
  const firstTurnRaw = useRef(true);
  useEffect(() => {
    if (firstTurnRaw.current) { firstTurnRaw.current = false; return; }
    void (async () => {
      const focusProcessId = await fetchSessionFocus();
      // The overview scope names no process; and while the landscape is
      // showing the screen stays where it is (096).
      if (focusProcessId && focusProcessId !== OVERVIEW_SCOPE && view === 'process') setFocusedProcessId(focusProcessId);
      await refreshRegister();
      await refreshProcessDoc();
    })();
    const retry = setTimeout(() => { void refreshProcessDoc(); }, 1500);
    return () => clearTimeout(retry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnRaw]);

  const effectiveFlowText = doc?.flowText || '';
  useEffect(() => { onDocumentChange?.(); }, [doc?.updatedAt, effectiveFlowText, onDocumentChange]);
  const hasFlow = Boolean(effectiveFlowText.trim());

  // 087 (Revision 3): two layers. The top card layer holds EVERY step in
  // temporal order — the first-pass capture, hydrating as the story is
  // told. The canvas below is the ORIGINAL flow experience, verbatim: the
  // domain flow captured in the original statement grammar during the
  // second-pass walk (actions tied to commands on contexts, events raised
  // and subscribed to, presented information), parsed and laid out by the
  // original renderer with no reinterpretation.
  const parsedSteps = useMemo(() => parseStepFlow(effectiveFlowText).steps, [effectiveFlowText]);
  const domainFlowText = doc?.domainFlowText || '';
  const [canvasViewport, setCanvasViewport] = useState<{ x: number; zoom: number } | null>(null);

  // 088 (Revision 2): the domain walk. The facilitator maintains the
  // pending pool while listening; the screen walks ONE part at a time,
  // one group at a time (who acts → which area handles it → what happens
  // next), with the active group ghosted on the canvas where it will
  // land. Accept, drop and rename are deterministic server calls (no
  // model turn); the document refresh brings map and band forward
  // together. The active part is the consultant's selection from the
  // step layer, else the first pending part.
  const assumptions = useMemo(() => doc?.domainAssumptions ?? [], [doc?.domainAssumptions]);
  const [selectedPart, setSelectedPart] = useState<number | null>(null);
  const appliedExternalStep = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (selectedStepId === undefined || selectedStepId === appliedExternalStep.current) return;
    if (selectedStepId !== null && !parsedSteps.length) return;
    appliedExternalStep.current = selectedStepId;
    setSelectedPart(selectedStepId === null ? null : partOfStep(parsedSteps, selectedStepId));
  }, [selectedStepId, parsedSteps]);
  const [skippedGroups, setSkippedGroups] = useState<Set<string>>(() => new Set());
  const [doneGroups, setDoneGroups] = useState<Set<string>>(() => new Set());
  const [walkBusy, setWalkBusy] = useState(false);
  const [editing, setEditing] = useState<{
    key: string; statementId: number; field: 'actor' | 'sys' | 'name'; value: string;
  } | null>(null);
  const selectedEntry = selectedPart === null ? undefined : assumptions.find((entry) => entry.part === selectedPart);
  const activeEntry = selectedEntry ?? (selectedPart === null ? assumptions[0] : undefined);
  const activePart = activeEntry?.part ?? selectedPart;
  const walkGroups = useMemo(
    () => (activeEntry ? deriveWalkGroups(activeEntry.lines, domainFlowText) : []),
    [activeEntry, domainFlowText],
  );
  const groupKey = (part: number, kind: string) => `${part}:${kind}`;
  const activeGroup = activeEntry
    ? (walkGroups.find((group) => !skippedGroups.has(groupKey(activeEntry.part, group.kind))) ?? walkGroups[0] ?? null)
    : null;
  const ghost = useMemo(
    () => (activeEntry && activeGroup
      ? planAcceptance(domainFlowText, activeEntry.lines, activeGroup.statementIds)
      : { ghostLines: [], ghostIds: [] as number[] }),
    [activeEntry, activeGroup, domainFlowText],
  );
  const ghostIds = useMemo(() => new Set(ghost.ghostIds), [ghost]);
  const canvasFlowText = [domainFlowText.trim(), ...ghost.ghostLines].filter(Boolean).join('\n');
  const hasCanvas = Boolean(canvasFlowText.trim());
  const domainRenderModel = useMemo(
    () => ({ ...session.newModel, flowText: canvasFlowText }),
    [session.newModel, canvasFlowText],
  );
  const highlightStepIds = useMemo(
    () => new Set(activePart === null ? [] : stepIdsOfPart(parsedSteps, activePart)),
    [parsedSteps, activePart],
  );
  const partLabel = activeEntry?.partLabel ?? parsedSteps.find((step) => step.id === activePart)?.label ?? '';
  const waitingParts = assumptions.filter((entry) => entry.part !== activePart);
  const progress = WALK_ORDER
    .map(({ kind, title }) => {
      const key = activePart === null ? '' : groupKey(activePart, kind);
      const present = walkGroups.some((group) => group.kind === kind);
      const state = doneGroups.has(key) ? 'done' : activeGroup?.kind === kind ? 'active' : present ? 'pending' : 'absent';
      return { kind, title, state };
    })
    .filter((step) => step.state !== 'absent');
  // Every initiator proposed anywhere in the pool, for "move this action to".
  const initiators = useMemo(() => {
    const names = new Set<string>();
    for (const entry of assumptions) {
      for (const line of entry.lines) {
        const match = line.match(/@[!^]([A-Za-z0-9_.-]+)\s*>\s*\(action\)/);
        if (match) names.add(match[1]);
      }
    }
    return [...names];
  }, [assumptions]);

  const walkCall = async (action: 'accept' | 'edit' | 'reject', payload: Record<string, unknown>) => {
    if (!currentEngagementId || !focusedProcessId || walkBusy) return;
    setWalkBusy(true);
    try {
      const response = await fetch(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/processes/${encodeURIComponent(focusedProcessId)}/assumptions/${action}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      if (response.ok) await refreshProcessDoc();
    } finally {
      setWalkBusy(false);
    }
  };
  const acceptGroup = async () => {
    if (!activeEntry || !activeGroup) return;
    const key = groupKey(activeEntry.part, activeGroup.kind);
    await walkCall('accept', { part: activeEntry.part, statementIds: activeGroup.statementIds });
    setDoneGroups((done) => new Set(done).add(key));
  };
  const skipGroup = () => {
    if (!activeEntry || !activeGroup) return;
    setSkippedGroups((skipped) => new Set(skipped).add(groupKey(activeEntry.part, activeGroup.kind)));
  };
  const saveEdit = () => {
    if (!editing || !activeEntry) return;
    const value = editing.value.trim();
    setEditing(null);
    if (!value) return;
    void walkCall('edit', { part: activeEntry.part, statementId: editing.statementId, [editing.field]: value });
  };
  const askToWalk = () => {
    if (!partLabel) return;
    void session.handleTextMessage(`Let's walk the domain for "${partLabel}" now.`);
  };
  const editField = (label: string) => (
    <span className="es-walk-edit">
      <input
        className="es-walk-input"
        aria-label={label}
        autoFocus
        value={editing?.value ?? ''}
        onChange={(e) => setEditing(editing ? { ...editing, value: e.target.value } : null)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') saveEdit();
          if (e.key === 'Escape') setEditing(null);
        }}
      />
      <button type="button" className="es-walk-accept" onClick={saveEdit}>Save</button>
      <button type="button" className="es-walk-ghost" onClick={() => setEditing(null)}>Cancel</button>
    </span>
  );

  // The register's plain projection for the sidebar and the landscape —
  // order preserved exactly as given, live and removed together (the
  // components split and sort them), with the business map's fields when
  // the register carries them.
  const navigationProcesses = register.processes.map((process) => {
    const extras = process as typeof process & RegisterExtras;
    return {
      processId: process.processId,
      name: process.name,
      stepCount: process.stepCount,
      state: process.state,
      removedAt: process.removedAt,
      order: process.order,
      domainId: extras.domainId ?? null,
      importance: extras.importance ?? null,
      rank: extras.rank ?? null,
    };
  });
  const focusedProcess = register.processes.find((process) => process.processId === focusedProcessId);
  const focusedDomainName = focusedProcess
    ? (domains.find((domain) => domain.id === (focusedProcess as typeof focusedProcess & RegisterExtras).domainId)?.name ?? UNPLACED_LABEL)
    : '';
  const openProcess = (processId: string) => {
    chooseProcess(processId);
    setSelectedPart(null);
  };
  const handleCreateProcess = async (name: string) => {
    const result = await createProcess(name);
    if (result?.processId) chooseProcess(result.processId);
  };

  // 096: the process navigation lives in the shell's sidebar when there is
  // one (a portal into the claimed slot), else inline beside the screen.
  const sidebarTarget = useShellSidebar(!embedded);
  const businessMapActivity = ((model.setup as { activities?: EngagementActivity[] } | null | undefined)?.activities ?? [])
    .find((entry) => entry.specialistMethod === 'domains-stakeholders');
  const navigation = (
    <ProcessNavigation
      engagementLabel={currentEngagement?.client || 'Engagement'}
      activityName={activity.name}
      backTo={`/engagement/activities/${encodeURIComponent(activity.id)}`}
      businessMapTo={businessMapActivity
        ? `/engagement/activities/${encodeURIComponent(businessMapActivity.id)}/specialist/domains-stakeholders`
        : '/engagement/activities'}
      processes={navigationProcesses}
      domains={domains}
      focusedProcessId={focusedProcessId}
      landscapeActive={view === 'landscape'}
      onLandscape={openLandscape}
      onSelect={openProcess}
      onCreate={(name) => { void handleCreateProcess(name); }}
      onRename={(processId, name) => { void renameProcess(processId, name); }}
      onRemove={(processId) => { void removeProcess(processId); }}
      onRestore={(processId) => { void restoreProcess(processId); }}
      onReorder={(processId, direction) => { void reorderProcess(processId, direction); }}
    />
  );

  const [checkpointOpen, setCheckpointOpen] = useState(false);
  const [checkpointLabel, setCheckpointLabel] = useState('');
  const submitCheckpoint = () => {
    const label = checkpointLabel.trim();
    setCheckpointOpen(false);
    setCheckpointLabel('');
    if (label) void takeCheckpoint(label);
  };

  const [savingFlowText, setSavingFlowText] = useState(false);
  const saveProcessSource = async (
    field: 'flowText' | 'domainFlowText',
    { flowText, expectedRevision }: FlowSourceSaveArgs,
  ): Promise<FlowSourceSaveResult> => {
    if (!currentEngagementId || !focusedProcessId) return { ok: false, error: 'No process is focused.' };
    if (expectedRevision === undefined) return { ok: false, error: 'The process revision is still loading. Close the editor and try again.' };
    setSavingFlowText(true);
    try {
      const parsed = parseStepFlow(field === 'flowText' ? flowText : (doc?.flowText || ''));
      const localIds: number[] = [];
      const collect = (steps: typeof parsed.steps) => steps.forEach((step) => { localIds.push(step.id); collect(step.steps); });
      collect(parsed.steps);
      const currentMap = doc?.stepIdentity?.byLocalId || {};
      const identityMap = Object.fromEntries(localIds.map((id) => [String(id), currentMap[String(id)] || null]));
      const retained = new Set(Object.values(identityMap).filter(Boolean));
      const retiredStepIds = Object.values(currentMap).filter((id) => !retained.has(id));
      const sourceText = `${field}:${flowText}`;
      let hash = 2166136261;
      for (let index = 0; index < sourceText.length; index += 1) hash = Math.imul(hash ^ sourceText.charCodeAt(index), 16777619);
      const response = await fetch(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/processes/${encodeURIComponent(focusedProcessId)}/flow-text`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contractVersion: 1, activityId: activity.id,
            commandId: `source-${focusedProcessId}-${expectedRevision}-${(hash >>> 0).toString(36)}`,
            expectedRevision, sources: { [field]: flowText },
            ...(flowText.trim() === '' ? { clearFields: [field] } : {}),
            ...(field === 'flowText' ? { identityMap, retiredStepIds } : {}),
          }),
        },
      );
      const data = await response.json().catch(() => ({} as Record<string, unknown>)) as {
        error?: string; errors?: Array<{ message?: string }>;
        details?: { diagnostics?: Array<{ message?: string }> };
        flowText?: string; domainFlowText?: string; revision?: string;
      };
      if (!response.ok) {
        if (response.status === 409) {
          await refreshProcessDoc();
          return {
            ok: false,
            status: 409,
            error: 'This process changed elsewhere. Your draft is preserved. Close and reopen the editor to review the latest process before saving.',
          };
        }
        if (response.status === 422) {
          const errors = Array.isArray(data.details?.diagnostics) ? data.details.diagnostics : Array.isArray(data.errors) ? data.errors : [];
          const message = errors.length > 0
            ? errors.map((e) => e.message).filter(Boolean).join('; ')
            : (data.error || 'The flow could not be saved.');
          return { ok: false, status: 422, error: message };
        }
        return { ok: false, status: response.status, error: data.error || `HTTP ${response.status}` };
      }
      const verification = await fetch(
        `/api/engagements/${encodeURIComponent(currentEngagementId)}/processes/${encodeURIComponent(focusedProcessId)}`,
      );
      const verified = verification.ok
        ? await verification.json().catch(() => ({})) as { doc?: { flowText?: string; domainFlowText?: string }; revision?: string }
        : {};
      const verifiedOwnWrite = typeof verified.revision === 'string'
        && verified.doc?.flowText === data.flowText
        && verified.doc?.domainFlowText === data.domainFlowText;
      await refreshProcessDoc();
      if (!verifiedOwnWrite) {
        return {
          ok: false,
          status: 409,
          error: 'The edit was saved, but the process changed again before its revision could be verified. Your draft is preserved; close and reopen the editor to review the latest process.',
        };
      }
      return { ok: true, revision: verified.revision };
    } catch (err) {
      return { ok: false, error: (err as Error).message };
    } finally {
      setSavingFlowText(false);
    }
  };

  return (
    <div className="journey-specialist-workspace journey-specialist-workspace--contextual">
      <div hidden={embedded} className="journey-specialist-context" aria-label="Activity context">
        <div>
          <span>Activity</span>
          <strong>{activity.name}</strong>
        </div>
        <div className="journey-specialist-context__actions">
          <Link to={`/engagement/activities/${encodeURIComponent(activity.id)}`}>Back to activity</Link>
          <Link to="/engagement/brief">Back to brief</Link>
        </div>
      </div>
      {sidebarTarget ? createPortal(navigation, sidebarTarget) : null}
      <div className="es-workspace-row">
      {sidebarTarget || embedded ? null : <div className="es-sidebar-fallback">{navigation}</div>}
      <ActivityShell
        embedded={embedded}
        title={view === 'landscape' ? 'Process landscape' : focusedProcess ? focusedProcess.name : 'Event Storming'}
        context={view === 'landscape'
          ? 'Explore how work connects. Choose a process to continue the workshop.'
          : focusedProcess
            ? `Process workspace · ${focusedDomainName}`
            : 'Choose a process from the side, or create one.'}
        conversation={{
          render: () => (sessionId ? (
            // Event Storming's chat is the discovery session, not the
            // facilitation companion (there is no Event Storming facilitation
            // bundle). It is the SAME dock and the SAME panel, and the shell
            // owns its open / collapse / expand / close states.
            <FacilitatorDock engagementId={currentEngagementId} activityId={activity.id} label="Event Storming conversation">
              <div className="es-scope" aria-label="Conversation scope">
                <span>Conversation scope</span>
                <strong>
                  {view === 'landscape'
                    ? 'Overview, all processes'
                    : focusedProcess ? focusedProcess.name : 'No process yet'}
                </strong>
              </div>
              <SessionCapturePanel
                session={session.chat}
                className="fshell-rail-panel"
                ariaLabel="Event Storming conversation"
                inputLabel={view === 'landscape' ? 'Ask about the landscape' : 'Tell the story'}
                emptyText={view === 'landscape'
                  ? 'Ask about the landscape: which processes share events, what is unassessed, where to storm next.'
                  : 'Talk or paste the process. Steps appear on the flow as you go.'}
              />
            </FacilitatorDock>
          ) : (
            <PendingActivityConversation
              facilitation={facilitation}
              label="Event Storming conversation"
              inputLabel="Tell the story"
              emptyText="Talk or paste the process. Steps appear on the flow as you go."
              engagementId={currentEngagementId}
              activityId={activity.id}
            />
          )),
          opening: facilitation.opening,
          error: facilitation.error || null,
          onRetry: facilitation.open,
        }}
        actions={(
          <>
          <nav className="es-crumbs" aria-label="Breadcrumb" hidden={embedded}>
            <span>Event Storming</span>
            {view === 'process' && focusedProcess ? (
              <>
                <span aria-hidden="true">/</span>
                <button type="button" onClick={openLandscape}>Process landscape</button>
                <span aria-hidden="true">/</span>
                <span aria-current="page">{focusedProcess.name}</span>
              </>
            ) : (
              <>
                <span aria-hidden="true">/</span>
                <span aria-current="page">{view === 'landscape' ? 'Process landscape' : 'All processes'}</span>
              </>
            )}
          </nav>
          {view === 'process' && focusedProcessId ? (
          <>
          <button type="button" className="fshell-close" disabled={!focusedProcessId} onClick={() => { void undo(); }}>
            Undo
          </button>
          {checkpointOpen ? (
            <span className="es-walk-edit">
              <input
                className="es-walk-input"
                aria-label="Checkpoint label"
                autoFocus
                value={checkpointLabel}
                onChange={(e) => setCheckpointLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitCheckpoint();
                  if (e.key === 'Escape') { setCheckpointOpen(false); setCheckpointLabel(''); }
                }}
              />
              <button type="button" className="es-walk-accept" onClick={submitCheckpoint}>Save</button>
              <button type="button" className="es-walk-ghost" onClick={() => { setCheckpointOpen(false); setCheckpointLabel(''); }}>
                Cancel
              </button>
            </span>
          ) : (
            <button type="button" className="fshell-close" disabled={!focusedProcessId} onClick={() => setCheckpointOpen(true)}>
              Checkpoint
            </button>
          )}
          <button type="button" className="fshell-close" disabled={!focusedProcessId || doc?.processId !== focusedProcessId || !docRevision} onClick={() => setSourceOpen(true)}>
            Edit flow source
          </button>
          </>
          ) : null}
          </>
        )}
      >
          <div className="es-flow-surface">
            {model.error ? <p className="fshell-alert" role="alert">{model.error.message}</p> : null}
            {processError ? (
              <p className="fshell-alert" role="status">
                {processError}{' '}
                <button type="button" className="es-walk-ghost" onClick={() => clearProcessError?.()}>Dismiss</button>
              </p>
            ) : null}
            {doc?.rejected ? (
              <div className="es-rejected-notice" role="alert">
                <p>
                  The last turn needs a look. Here is what came through and
                  why it was not saved. The process stays exactly as it was.
                </p>
                {doc.rejected.raw != null ? <blockquote className="es-rejected-raw">{rejectedCaptureText(doc.rejected.raw)}</blockquote> : null}
                {doc.rejected.errors?.length ? (
                  <ul className="es-rejected-errors">
                    {doc.rejected.errors.map((error, index) => (
                      <li key={index}>{error.message}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            {view === 'landscape' ? (
              <ProcessLandscape
                processes={navigationProcesses}
                glossary={glossary as LandscapeGlossary | undefined}
                domains={domains}
                focusedProcessId={focusedProcessId}
                onOpen={openProcess}
                filters={landscapeFilters}
                onFiltersChange={changeLandscapeFilters}
                view={landscapeView}
                onViewChange={(view) => setSearchParams(previous => {
                  const next = new URLSearchParams(previous);
                  next.set('zoom', String(view.zoom)); next.set('left', String(view.left)); next.set('top', String(view.top));
                  return next;
                }, { replace: true })}
              />
            ) : hasFlow ? (
              <div className="es-blueprint">
                <StepCardRibbon
                  steps={parsedSteps}
                  viewport={hasCanvas ? canvasViewport : null}
                  highlightStepIds={highlightStepIds}
                  onSelectStep={(stepId) => setSelectedPart(partOfStep(parsedSteps, stepId))}
                />
                {(activeEntry || selectedPart !== null) && (
                  <section className="es-walk" aria-label="Domain walk">
                    <div className="es-walk-strip">
                      <span className="es-walk-part">Domain walk · {partLabel}</span>
                      {progress.length > 0 && (
                        <ol className="es-walk-progress">
                          {progress.map((step) => (
                            <li key={step.kind} className={`es-walk-step es-walk-step--${step.state}`}>{step.title}</li>
                          ))}
                        </ol>
                      )}
                      {waitingParts.length > 0 && (
                        <span className="es-walk-then">
                          then {waitingParts.map((entry) => entry.partLabel).join(', ')}
                        </span>
                      )}
                      {activePart !== null && movePart && (
                        <select
                          className="es-walk-move"
                          aria-label={`Move "${partLabel}" to`}
                          value=""
                          disabled={walkBusy}
                          onChange={(e) => {
                            const choice = e.target.value;
                            if (!choice) return;
                            void (async () => {
                              const result = choice === '__new__'
                                ? await movePart(activePart, { newProcessName: partLabel })
                                : await movePart(activePart, { toProcessId: choice });
                              setSelectedPart(null);
                              await refreshRegister();
                              if (result?.created) setFocusedProcessId(result.targetProcessId);
                            })();
                          }}
                        >
                          <option value="">Move to…</option>
                          {register.processes
                            .filter((process) => !process.removedAt && process.processId !== focusedProcessId)
                            .sort((a, b) => a.order - b.order)
                            .map((process) => (
                              <option key={process.processId} value={process.processId}>{process.name}</option>
                            ))}
                          <option value="__new__">a new process</option>
                        </select>
                      )}
                    </div>
                    {activeEntry && activeGroup ? (
                      <>
                        <p className="es-walk-question">{activeGroup.question}</p>
                        <ul className="es-walk-concepts">
                          {activeGroup.concepts.map((concept) => {
                            const chipKey = `actor:${concept.rawName}`;
                            return (
                              <li key={concept.rawName} className="es-walk-concept">
                                {editing?.key === chipKey ? editField('Name') : (
                                  <button
                                    type="button"
                                    className={`es-walk-chip es-walk-chip--${concept.kind}`}
                                    title="Rename"
                                    disabled={walkBusy}
                                    onClick={() => setEditing({
                                      key: chipKey, statementId: concept.rows[0].id, field: 'actor', value: concept.name,
                                    })}
                                  >
                                    {concept.name}
                                  </button>
                                )}
                                <ul className="es-walk-rows">
                                  {concept.rows.map((row) => (
                                    <li key={row.id} className="es-walk-row">
                                      {editing?.key === `name:${row.id}` ? editField('What happens') : (
                                        <button
                                          type="button"
                                          className="es-walk-row-text"
                                          title="Rename"
                                          disabled={walkBusy}
                                          onClick={() => setEditing({
                                            key: `name:${row.id}`, statementId: row.id, field: 'name', value: humaniseName(row.rawName),
                                          })}
                                        >
                                          {row.text}
                                        </button>
                                      )}
                                      {row.raised.map((event) => (
                                        editing?.key === `event:${event.id}` ? (
                                          <span key={event.id}>{editField(event.shown ? 'What is shown' : 'What it raises')}</span>
                                        ) : (
                                          <button
                                            key={event.id}
                                            type="button"
                                            className="es-walk-chip es-walk-chip--event"
                                            title="Rename"
                                            disabled={walkBusy}
                                            onClick={() => setEditing({
                                              key: `event:${event.id}`, statementId: event.id, field: 'name', value: event.name,
                                            })}
                                          >
                                            {event.shown ? 'showing' : 'raising'} “{event.name}”
                                          </button>
                                        )
                                      ))}
                                      {row.rawSys.map((sys) => (
                                        editing?.key === `sys:${row.id}:${sys}` ? (
                                          <span key={sys}>{editField('Touchpoint')}</span>
                                        ) : (
                                          <button
                                            key={sys}
                                            type="button"
                                            className="es-walk-chip es-walk-chip--sys"
                                            title="Rename touchpoint"
                                            disabled={walkBusy}
                                            onClick={() => setEditing({
                                              key: `sys:${row.id}:${sys}`, statementId: row.id, field: 'sys', value: sys,
                                            })}
                                          >
                                            through {humaniseName(sys)}
                                          </button>
                                        )
                                      ))}
                                      {activeGroup.kind === 'who-acts' && initiators.length > 1 && (
                                        <select
                                          className="es-walk-move"
                                          aria-label={`Move "${row.text}" to`}
                                          value=""
                                          disabled={walkBusy}
                                          onChange={(e) => {
                                            if (e.target.value) {
                                              void walkCall('edit', { part: activeEntry.part, statementId: row.id, moveTo: e.target.value });
                                            }
                                          }}
                                        >
                                          <option value="">Move to…</option>
                                          {initiators.filter((name) => name !== concept.rawName).map((name) => (
                                            <option key={name} value={name}>{humaniseName(name)}</option>
                                          ))}
                                        </select>
                                      )}
                                      <button
                                        type="button"
                                        className="es-walk-ghost"
                                        aria-label={`Not this: ${row.text}`}
                                        disabled={walkBusy}
                                        onClick={() => { void walkCall('reject', { part: activeEntry.part, statementId: row.id }); }}
                                      >
                                        Not this
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </li>
                            );
                          })}
                        </ul>
                        <div className="es-walk-actions">
                          <span className="es-walk-hint">
                            Click a name to rename it. Saying it in the conversation works too.
                          </span>
                          <button type="button" className="es-walk-ghost" disabled={walkBusy} onClick={skipGroup}>
                            Skip
                          </button>
                          <button type="button" className="es-walk-accept" disabled={walkBusy} onClick={() => { void acceptGroup(); }}>
                            Accept this group
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="es-walk-empty">
                        Nothing proposed for this part yet.{' '}
                        <button type="button" className="es-walk-ghost" onClick={askToWalk}>
                          Ask the facilitator to walk this part
                        </button>
                      </p>
                    )}
                  </section>
                )}
                <div className="es-domain-region">
                  {hasCanvas ? (
                    <FlowViewBoundary resetKey={canvasFlowText}>
                      <FlowCanvas
                        newModel={domainRenderModel}
                        showNavigation={false}
                        ghostIds={ghostIds}
                        contentScopeKey={focusedProcessId}
                        contentRevision={docRevision}
                        liveAdditionSignal={userTurnCount}
                        liveAdditionActive={session.isLoading}
                        onViewportChange={({ x, zoom }) => setCanvasViewport({ x, zoom })}
                      />
                    </FlowViewBoundary>
                  ) : (
                    <p className="es-domain-empty">
                      The domain builds here on the second pass: as actors,
                      bounded contexts and systems are captured, each step
                      lands in its lane beneath the story above.
                    </p>
                  )}
                </div>
              </div>
            ) : focusedProcessId ? (
              <p className="outline-plan-empty">
                No steps yet. Talk the process through, and the flow builds
                here as the story arrives.
              </p>
            ) : null}
          </div>
      </ActivityShell>
      </div>
      <FlowSourceEditModal
        isOpen={sourceOpen}
        onClose={() => setSourceOpen(false)}
        sessionId={sessionId}
        currentFlowText={effectiveFlowText}
        onSave={(args) => saveProcessSource('flowText', args)}
        isSaving={savingFlowText}
        sourceScopeKey={currentEngagementId && focusedProcessId ? `${currentEngagementId}:${focusedProcessId}` : undefined}
        sources={[
          {
            grammar: 'steps',
            label: 'Process steps',
            currentFlowText: effectiveFlowText,
            revision: docRevision,
            strict: true,
            onSave: (args) => saveProcessSource('flowText', args),
          },
          {
            grammar: 'domain',
            label: 'Domain interactions',
            currentFlowText: domainFlowText,
            revision: docRevision,
            strict: true,
            onSave: (args) => saveProcessSource('domainFlowText', args),
          },
        ]}
      />
    </div>
  );
}
